import { useState, type ReactNode } from 'react';
import type { WorkbookDefinition } from '@erp/shared-types';
import {
  ArrowSquareOutIcon,
  CaretDownIcon,
  CaretRightIcon,
  DotsThreeIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { Button } from '@/components/ui/button';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useExecutionLabel } from './useExecutionLabel';
import type { OutlineCell, OutlineInstruction } from '@erp/workbooks';
import { WorkbookProcessInstruction } from './WorkbookProcessInstruction';
import { WorkbookInstructionEditor } from './WorkbookInstructionEditor';
import { WorkbookStepDescription } from './WorkbookStepDescription';
import { WorkbookStepConfiguration } from './WorkbookStepConfiguration';
import {
  WorkbookGenerateStepButton,
  WorkbookGenerationStatus,
} from './WorkbookGeneration';
import {
  AddInstructionButton,
  HEAD_ACTIONS,
  STEP_ACTIONS_TRIGGER,
  STEP_GUTTER,
} from './WorkbookProcessContext';
import { useWorkbookProcess } from './useWorkbookProcess';
import {
  WorkbookRegenerateSopButton,
  WorkbookSopReviewUnavailable,
} from './WorkbookSopUpdateDialog';
import { IconTooltip } from '@/components/common/IconTooltip';

/** One step of the Process: its name and plain-English description, no Result preview. */
export function WorkbookProcessCell({ cell }: { cell: OutlineCell }) {
  const {
    definition,
    references,
    canUpdate,
    openItem,
    editingId,
    setEditingId,
    generation,
    insert,
    renderRunMenu,
  } = useWorkbookProcess();
  const gt = useGT();
  const { label, cellTitle } = useExecutionLabel(references);
  const { head: node, instructions } = cell;
  const itemId = node.kind === 'instruction' ? undefined : node.itemId;
  const first = instructions[0];
  const title = cellTitle(cell);
  const active = instructions.some((item) => item.id === editingId);
  function edit() {
    if (!canUpdate || generation.generating) return;
    if (first) setEditingId(first.id);
    else insert(node.id);
  }
  return (
    <li
      id={`process-node-${node.id}`}
      className={cn(
        'min-w-0 scroll-mt-3 rounded-lg border border-input bg-card',
        active && 'border-ring',
      )}
    >
      <div className="group/head flex items-center gap-2 rounded-t-lg border-b border-input bg-muted/70 px-3 py-2">
        <h3 className="min-w-0 flex-1 text-sm font-semibold">
          {canUpdate ? (
            <button
              type="button"
              onClick={edit}
              disabled={generation.generating}
              className="rounded text-left hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              aria-label={gt('Edit {name}', {
                context: 'workbook edit named instruction',
                name: title,
              })}
            >
              {title}
            </button>
          ) : (
            title
          )}
        </h3>
        <div className={HEAD_ACTIONS}>
          {node.kind !== 'instruction' && renderRunMenu?.(node)}
          {itemId && (
            <IconTooltip>
              <Button
                size="icon"
                variant="ghost"
                className="size-7"
                onClick={() => openItem(itemId)}
                aria-label={gt('Open {name}', {
                  context: 'workbook open execution item',
                  name: label(node),
                })}
              >
                <ArrowSquareOutIcon />
              </Button>
            </IconTooltip>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3 p-3">
        {node.kind !== 'instruction' && !instructions.length && (
          <WorkbookStepDescription
            definition={definition}
            references={references}
            root={node}
          />
        )}
        <WorkbookCellInstructions cell={cell} />
        {!instructions.length && <AddInstructionButton after={node} />}
        {itemId && instructions.length > 0 && (
          <WorkbookCellSettings itemId={itemId} />
        )}
      </div>
    </li>
  );
}

/**
 * What a Worksheet cell's settings do, collapsed under its Instructions: the
 * filters, formulas and sort they describe, to compare against the text.
 */
function WorkbookCellSettings({ itemId }: { itemId: string }) {
  const { definition, references, openItem } = useWorkbookProcess();
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 rounded text-xs text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring">
        {open ? (
          <CaretDownIcon className="size-3.5" />
        ) : (
          <CaretRightIcon className="size-3.5" />
        )}
        <T context="workbook process configuration disclosure">Settings</T>
      </CollapsibleTrigger>
      <CollapsibleContent className="text-xs text-muted-foreground">
        <WorkbookStepConfiguration
          definition={definition}
          references={references}
          itemIds={[itemId]}
          openItem={openItem}
          summaryOnly
        />
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * A cell's Instructions, each read-only with its actions or open in its editor.
 * `showHeadTitle` shows the first one's title too, where no cell header shows it.
 * `standalone` renders them outside the Process (an Item's own Instructions):
 * no anchors to scroll to and no nesting indent.
 */
export function WorkbookCellInstructions({
  cell,
  showHeadTitle = false,
  standalone = false,
}: {
  cell: OutlineCell;
  showHeadTitle?: boolean;
  standalone?: boolean;
}) {
  const { canUpdate, editingId, generation, reviewCheck } =
    useWorkbookProcess();
  const { head: node, instructions } = cell;
  const requested = instructions.some(
    (item) => item.id === generation.requestedStepId,
  );
  const canEdit = canUpdate && !generation.generating;
  return (
    <>
      {instructions.map((instruction, index) => (
        <div
          key={instruction.id}
          id={
            standalone || instruction.id === node.id
              ? undefined
              : `process-node-${instruction.id}`
          }
          style={{
            marginLeft: standalone
              ? 0
              : Math.max(
                  0,
                  instruction.depth - (node.kind === 'instruction' ? 0 : 1),
                ) * 20,
          }}
          className={cn(
            'group/step relative flex min-w-0 scroll-mt-3 flex-col gap-2',
            STEP_GUTTER,
          )}
        >
          {instruction.step.reviewStatus && (
            <WorkbookAlignmentNote
              stepId={instruction.id}
              status={instruction.step.reviewStatus}
              check={reviewCheck?.(instruction.id)}
              canRegenerate={canEdit}
            />
          )}
          {editingId === instruction.id ? (
            <WorkbookProcessInstruction node={instruction} />
          ) : (
            <WorkbookInstructionView
              instruction={instruction}
              showTitle={index > 0 || showHeadTitle}
            />
          )}
        </div>
      ))}
      {requested && generation.generating && <WorkbookGenerationStatus />}
      {requested && generation.error && (
        <p role="alert" className="text-sm text-destructive">
          {generation.error}
        </p>
      )}
    </>
  );
}

/** One Instruction read-only, with its actions: clicking its title or text opens its editor. */
function WorkbookInstructionView({
  instruction,
  showTitle,
}: {
  instruction: OutlineInstruction;
  showTitle: boolean;
}) {
  const { references, canUpdate, setEditingId, generation, renderStepActions } =
    useWorkbookProcess();
  const gt = useGT();
  const { label } = useExecutionLabel(references);
  const canEdit = canUpdate && !generation.generating;
  return (
    <>
      {showTitle && instruction.step.title && (
        <h4 className="text-sm font-medium">
          {canEdit ? (
            <button
              type="button"
              onClick={() => setEditingId(instruction.id)}
              className="rounded text-left hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              aria-label={gt('Edit {name}', {
                context: 'workbook edit named instruction',
                name: instruction.step.title,
              })}
            >
              {instruction.step.title}
            </button>
          ) : (
            instruction.step.title
          )}
        </h4>
      )}
      {/* Clicking the text opens its editor; a Reference chip inside opens its Item instead. */}
      <div
        className={cn(canEdit && 'cursor-text')}
        onClick={(event) => {
          const onChip =
            event.target instanceof Element &&
            event.target.closest('button, a');
          if (canEdit && !onChip) setEditingId(instruction.id);
        }}
      >
        <WorkbookInstructionEditor
          value={instruction.step.instructions}
          readOnly
          mentionSources={references.mentionSources}
          label={gt('Step instruction', {
            context: 'workbook process step editor',
          })}
          onChange={() => undefined}
        />
      </div>
      {instruction.step.provenance && <WorkbookAiUpdatedBadge />}
      {canUpdate && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className={STEP_ACTIONS_TRIGGER}
              aria-label={gt('Actions for {name}', {
                context: 'workbook process step actions menu',
                name: label(instruction),
              })}
            >
              <DotsThreeIcon />
            </Button>
          </PopoverTrigger>
          {/* Kept mounted: an action's in-flight request and retry key (Assign
              task's requestId) must outlive the menu closing. */}
          <PopoverContent
            keepMounted
            align="end"
            className="flex w-auto max-w-[min(32rem,calc(100vw-2rem))] flex-wrap items-center gap-1 p-1"
          >
            <Button
              size="sm"
              variant="ghost"
              disabled={generation.generating}
              onClick={() => setEditingId(instruction.id)}
              aria-label={gt('Edit instruction {name}', {
                context: 'workbook notebook edit instruction',
                name: label(instruction),
              })}
            >
              <PencilSimpleIcon />
              <T context="workbook edit process step action">Edit</T>
            </Button>
            <WorkbookGenerateStepButton
              id={instruction.id}
              step={instruction.step}
            />
            {renderStepActions?.(instruction.id)}
            <AddInstructionButton after={instruction} />
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

/** Marks text the AI rewrote from the settings (an accepted SOP update) until the user edits it. */
function WorkbookAiUpdatedBadge() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className="self-start font-normal">
          <T context="workbook instruction ai updated badge">AI-updated</T>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <T context="workbook instruction ai updated tooltip">
          Rewritten by AI from the settings. Edit it to make it your own.
        </T>
      </TooltipContent>
    </Tooltip>
  );
}

function WorkbookAlignmentNote({
  stepId,
  status,
  check,
  canRegenerate,
}: {
  stepId: string;
  status: NonNullable<
    WorkbookDefinition['processSteps'][number]['reviewStatus']
  >;
  /** What the AI check says about it, where one runs. */
  check?: ReactNode;
  canRegenerate: boolean;
}) {
  return (
    <AlertContainer
      role="status"
      className="flex flex-wrap items-start justify-between gap-2 p-2"
    >
      <AlertDescription className="min-w-0 flex-1">
        {status === 'instruction-edited' ? (
          <T context="workbook notebook instruction pending">
            Instruction changed. Generate to apply it; Run still uses the
            current workbook settings.
          </T>
        ) : status === 'both-edited' ? (
          <T context="workbook notebook both changed">
            Instruction and workbook settings both changed. Review both before
            generating; Run uses the current workbook settings.
          </T>
        ) : (
          <T context="workbook notebook execution pending">
            Workbook settings changed since this instruction was written.
            Regenerate it from the settings, or Generate to apply the
            instruction to them instead.
          </T>
        )}
        {check}
        {status === 'configuration-edited' && (
          <WorkbookSopReviewUnavailable stepId={stepId} />
        )}
      </AlertDescription>
      {canRegenerate && status === 'configuration-edited' && (
        <WorkbookRegenerateSopButton stepIds={[stepId]} />
      )}
    </AlertContainer>
  );
}
